import { create } from 'zustand';

import { IMappedInvoiceAdjustmentsItem } from '~apis/mapping';
import { CashFlowType } from '~models/cashFlow';

type TargetTaxInvoice = {
  date: string;
  amount: number;
  businessRegistrationNumber: string;
  counterPartyName: string;
  repItem: string;
};

export type MappingTransaction = {
  cashFlowType: CashFlowType;
  labelId: number;
  transactionAt: string;
  transactionName: string;
  transactionDescription: string;
  totalAmount: number;
  mappingAmount: number;
  categoryLabelKey: string | null;
  groupLabelId: number | null;
  transactionId: number;
  isFx?: boolean;
  itemCashFlowType: CashFlowType;
  labelAmount: number;
};

export type AdjustmentTaxInvoice = Partial<
  IMappedInvoiceAdjustmentsItem & {
    cashFlowType: CashFlowType;
  }
> | null;

interface SettlementMappingStore {
  targetTaxInvoice: TargetTaxInvoice | null;
  mappingTransactions: MappingTransaction[];
  originalMappingTransactions: MappingTransaction[];
  adjustmentTaxInvoices: AdjustmentTaxInvoice[];
  setMappingTransaction: (mappingTransaction: MappingTransaction) => void;
  deleteMappingTransaction: (idx: number) => void;
  addAdjustmentTaxInvoice: (reportingDate: string, adjustmentAmount: number) => void;
  setAdjustmentTaxInvoice: (adjustmentTaxInvoice: AdjustmentTaxInvoice, idx: number) => void;
  deleteAdjustmentTaxInvoice: (idx: number) => void;
  editMappingTransaction: (mappingTransaction: MappingTransaction, idx: number) => void;
  initialize: (data: {
    mappingTransactions: MappingTransaction[];
    originalMappingTransactions: MappingTransaction[];
    adjustmentTaxInvoices: AdjustmentTaxInvoice[];
  }) => void;
  reset: () => void;
}

const initialState: SettlementMappingStore = {
  mappingTransactions: [],
  originalMappingTransactions: [],
  adjustmentTaxInvoices: [],
  targetTaxInvoice: null,
  setMappingTransaction: () => {},
  editMappingTransaction: () => {},
  deleteMappingTransaction: () => {},
  addAdjustmentTaxInvoice: () => {},
  setAdjustmentTaxInvoice: () => {},
  deleteAdjustmentTaxInvoice: () => {},
  initialize: () => {},
  reset: () => {},
};

const useSettlementMappingStore = create<SettlementMappingStore>((set) => ({
  ...initialState,
  reset: () =>
    set((state) => ({
      ...state,
      adjustmentTaxInvoices: [],
    })),
  initialize: (data: {
    mappingTransactions: MappingTransaction[];
    originalMappingTransactions: MappingTransaction[];
    adjustmentTaxInvoices: AdjustmentTaxInvoice[];
  }) => set(() => data),
  setTargetTaxInvoice: (targetTaxInvoice: TargetTaxInvoice) => set(() => ({ targetTaxInvoice })),
  setMappingTransaction: (mappingTransaction: MappingTransaction) =>
    set((state) => ({
      mappingTransactions: [...state.mappingTransactions, mappingTransaction],
    })),
  editMappingTransaction: (mappingTransaction: MappingTransaction, idx: number) =>
    set((state) => ({
      mappingTransactions: [
        ...state.mappingTransactions.slice(0, idx),
        {
          ...state.mappingTransactions[idx],
          ...mappingTransaction,
        },
        ...state.mappingTransactions.slice(idx + 1),
      ],
    })),
  deleteMappingTransaction: (idx: number) =>
    set((state) => ({
      mappingTransactions: [
        ...state.mappingTransactions.slice(0, idx),
        ...state.mappingTransactions.slice(idx + 1),
      ],
    })),
  addAdjustmentTaxInvoice: (reportingDate: string, adjustmentAmount: number) => {
    set((state) => ({
      adjustmentTaxInvoices: [
        ...state.adjustmentTaxInvoices,
        {
          reportingDate,
          cashFlowType: CashFlowType.IN,
          adjustmentAmount,
        },
      ],
    }));
  },
  setAdjustmentTaxInvoice: (adjustmentTaxInvoice: AdjustmentTaxInvoice, idx: number) =>
    set((state) => ({
      adjustmentTaxInvoices: [
        ...state.adjustmentTaxInvoices.slice(0, idx),
        {
          ...state.adjustmentTaxInvoices[idx],
          ...adjustmentTaxInvoice,
        },
        ...state.adjustmentTaxInvoices.slice(idx + 1),
      ],
    })),
  deleteAdjustmentTaxInvoice: (idx: number) =>
    set((state) => ({
      adjustmentTaxInvoices: [
        ...state.adjustmentTaxInvoices.slice(0, idx),
        ...state.adjustmentTaxInvoices.slice(idx + 1),
      ],
    })),
}));

export default useSettlementMappingStore;
